import { useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { useLogger } from '@/lib/logger';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { trpc } from '@/lib/trpc';

interface UseConsumptionReportParams {
  organizationId: string | null;
  startDate: Date;
  endDate: Date;
  locationId: string | null;
  enabled?: boolean;
}

interface UseDailyConsumptionParams extends UseConsumptionReportParams {
  typeFilter: string[];
  colorFilter: string[];
  sizeFilter: string[];
  skuFilter: string[] | null;
}

/**
 * Hook for consumption summary data
 * Fetches and transforms SKU sales data with React Query caching
 */
export function useConsumptionSummary(params: UseConsumptionReportParams) {
  const enabled = params.enabled !== false && Boolean(params.organizationId);

  const { data, isLoading, error } = trpc.reporting.getConsumptionSummary.useQuery(
    {
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      locationId: params.locationId ?? null,
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      select: (rawData) => {
        const rows = rawData.map((item: { total_quantity: number | string; unique_orders: number | string; sales_velocity: number | string; sku: string; type: string; color: string | null; size: string | null; first_sale: string; last_sale: string; }) => {
          const totalQuantity =
            typeof item.total_quantity === 'number'
              ? item.total_quantity
              : parseInt(item.total_quantity, 10);
          const uniqueOrders =
            typeof item.unique_orders === 'number'
              ? item.unique_orders
              : parseInt(item.unique_orders, 10);
          // BLU-621: SQL-computed velocity is now units / full-window days
          // (period denominator) — comparable across SKUs. (Previously it
          // divided by active selling-days, which over-stated burst sellers.)
          const salesVelocity =
            typeof item.sales_velocity === 'number'
              ? item.sales_velocity
              : parseFloat(String(item.sales_velocity)) || 0;

          return {
            sku: item.sku,
            total_quantity: totalQuantity,
            unique_orders: uniqueOrders,
            type: item.type,
            color: item.color,
            size: item.size,
            first_sale: item.first_sale,
            last_sale: item.last_sale,
            sales_velocity: salesVelocity,
            trend: 'stable' as const, // placeholder — assigned below
          };
        });

        // Classify velocity tiers relative to the dataset
        return rows.map((row) => ({
          ...row,
          trend: classifyVelocityTier(row.sales_velocity, rows),
        }));
      },
    }
  );

  // Handle errors with toast and Sentry (effect to avoid setState during render)
  useEffect(() => {
    if (!error) return;

    Sentry.captureException(error, {
      tags: {
        feature: 'reporting',
        operation: 'consumption_summary',
        location_id: params.locationId,
      },
    });

    toast({
      title: 'Error',
      description: 'Failed to fetch consumption data',
      variant: 'destructive',
    });
  }, [error, params.locationId]);

  return {
    salesData: data || [],
    loading: isLoading,
    error,
  };
}

/**
 * Hook for inventory flow data (Sales, Transfers, Adjustments)
 */
export function useInventoryFlow(params: UseConsumptionReportParams) {
  const logger = useLogger();

  const enabled = params.enabled !== false && Boolean(params.organizationId);

  const { data, isLoading, error } = trpc.reporting.getInventoryFlow.useQuery(
    {
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      locationId: params.locationId ?? null,
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      select: (rawData) =>
        rawData.map((item: { flow_date: string; sold_quantity: number | string; transferred_out_quantity: number | string; transferred_in_quantity: number | string; adjustment_out_quantity: number | string; adjustment_in_quantity: number | string; po_received_quantity: number | string; }) => ({
          date: format(new Date(item.flow_date), 'MMM d'),
          sold_quantity: Number(item.sold_quantity),
          transferred_out_quantity: Number(item.transferred_out_quantity),
          transferred_in_quantity: Number(item.transferred_in_quantity),
          adjustment_out_quantity: Number(item.adjustment_out_quantity),
          adjustment_in_quantity: Number(item.adjustment_in_quantity),
          po_received_quantity: Number(item.po_received_quantity),
        })),
    }
  );

  useEffect(() => {
    if (!error) return;

    Sentry.captureException(error, {
      tags: {
        feature: 'reporting',
        operation: 'inventory_flow',
        location_id: params.locationId,
      },
    });
    logger.error('Inventory flow fetch failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }, [error, params.locationId, logger]);

  return {
    flowData: data || [],
    loading: isLoading,
    error,
  };
}

/**
 * Hook for stock value by category (current snapshot)
 */
export function useStockValueByCategory(params: UseConsumptionReportParams) {
  const logger = useLogger();

  const enabled = params.enabled !== false && Boolean(params.organizationId);

  const { data, isLoading, error } = trpc.reporting.getStockValueByCategory.useQuery(
    {
      locationId: params.locationId ?? null,
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      select: (rawData) =>
        rawData.map((item: {
          category: string | null;
          on_hand_quantity: number | string | null;
          stock_value: number | string | null;
          missing_cost_skus: number | string | null;
        }) => ({
          category: item.category ?? 'Uncategorized',
          onHandQuantity: Number(item.on_hand_quantity ?? 0),
          stockValue: item.stock_value === null ? null : Number(item.stock_value),
          missingCostSkus: Number(item.missing_cost_skus ?? 0),
        })),
    }
  );

  useEffect(() => {
    if (!error) return;

    Sentry.captureException(error, {
      tags: {
        feature: 'reporting',
        operation: 'stock_value_by_category',
        location_id: params.locationId,
      },
    });
    logger.error('Stock value by category fetch failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }, [error, params.locationId, logger]);

  return {
    stockValueData: data || [],
    loading: isLoading,
    error,
  };
}

/**
 * Hook for daily consumption trend data
 * Supports filtering by type, color, and size
 */
export function useDailyConsumption(params: UseDailyConsumptionParams) {
  const logger = useLogger();

  const enabled =
    params.enabled !== false &&
    Boolean(params.organizationId) &&
    (params.skuFilter === null || params.skuFilter.length > 0);

  const { data, isLoading, error } = trpc.reporting.getDailyConsumption.useQuery(
    {
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      locationId: params.locationId ?? null,
      typeFilter: params.typeFilter,
      colorFilter: params.colorFilter,
      sizeFilter: params.sizeFilter,
      skuFilter: params.skuFilter,
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      select: (rawData) =>
        rawData.map((item: { consumption_date: string; total_quantity: number | string; unique_orders: number | string; }) => ({
          date: format(new Date(item.consumption_date), 'MMM d'),
          quantity:
            typeof item.total_quantity === 'number'
              ? item.total_quantity
              : parseInt(item.total_quantity, 10),
          orders:
            typeof item.unique_orders === 'number'
              ? item.unique_orders
              : parseInt(item.unique_orders, 10),
        })),
    }
  );

  // Errors are logged but don't block the UI
  useEffect(() => {
    if (!error) return;

    const err = error instanceof Error ? error : new Error('Unknown error');

    Sentry.captureException(error, {
      tags: {
        feature: 'reporting',
        operation: 'daily_consumption',
        location_id: params.locationId,
      },
      level: 'warning', // Less critical than summary
    });

    logger.error('Daily consumption fetch failed', {
      operation: 'fetch_daily_consumption',
      error: err.message,
      locationId: params.locationId ?? undefined,
      dateRange: {
        start: params.startDate.toISOString(),
        end: params.endDate.toISOString(),
      },
      filters: {
        type: params.typeFilter,
        color: params.colorFilter,
        size: params.sizeFilter,
      },
    });
  }, [
    error,
    params.locationId,
    params.startDate,
    params.endDate,
    params.typeFilter,
    params.colorFilter,
    params.sizeFilter,
    logger,
  ]);

  return {
    dailyData: data || [],
    loading: isLoading,
    error,
  };
}

/**
 * Hook for unmatched SKUs summary
 * Fetches SKUs that couldn't be matched during order processing
 */
export function useUnmatchedSkus(params: UseConsumptionReportParams) {
  const logger = useLogger();

  const enabled = params.enabled !== false && Boolean(params.organizationId);

  const { data, isLoading, error } = trpc.reporting.getUnmatchedSkus.useQuery(
    {
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      locationId: params.locationId ?? null,
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      select: (rawData) =>
        rawData.map((item: {
          sku: string;
          occurrence_count: number | string;
          order_count: number | string;
          first_seen: string;
          last_seen: string;
          sample_order_ids: number[];
        }) => ({
          sku: item.sku,
          occurrenceCount: typeof item.occurrence_count === 'number'
            ? item.occurrence_count
            : parseInt(item.occurrence_count, 10),
          orderCount: typeof item.order_count === 'number'
            ? item.order_count
            : parseInt(item.order_count, 10),
          firstSeen: item.first_seen,
          lastSeen: item.last_seen,
          sampleOrderIds: item.sample_order_ids || [],
        })),
    }
  );

  // Handle errors with Sentry logging
  useEffect(() => {
    if (!error) return;

    Sentry.captureException(error, {
      tags: {
        feature: 'reporting',
        operation: 'unmatched_skus',
        location_id: params.locationId,
      },
    });

    logger.error('Unmatched SKUs fetch failed', {
      operation: 'fetch_unmatched_skus',
      error: error instanceof Error ? error.message : String(error),
      locationId: params.locationId ?? undefined,
    });

    toast({
      title: 'Error',
      description: 'Failed to fetch unmatched SKUs data',
      variant: 'destructive',
    });
  }, [error, params.locationId, logger]);

  return {
    unmatchedSkus: data || [],
    loading: isLoading,
    error,
  };
}

export type UnmatchedSkuData = {
  sku: string;
  occurrenceCount: number;
  orderCount: number;
  firstSeen: string;
  lastSeen: string;
  sampleOrderIds: number[];
};

/**
 * Classify velocity tier relative to the dataset using percentiles.
 * Top 25% = "growing" (high velocity), bottom 25% = "declining" (low velocity),
 * middle 50% = "stable". This adapts to actual data instead of using
 * hard-coded thresholds that are meaningless across different businesses.
 */
function classifyVelocityTier(
  velocity: number,
  allRows: { sales_velocity: number }[]
): 'growing' | 'stable' | 'declining' {
  if (velocity <= 0) return 'declining';
  if (allRows.length < 4) return 'stable';

  const velocities = allRows
    .map((r) => r.sales_velocity)
    .filter((v) => v > 0)
    .sort((a, b) => a - b);

  if (velocities.length < 4) return 'stable';

  const p25 = velocities[Math.floor(velocities.length * 0.25)]!;
  const p75 = velocities[Math.floor(velocities.length * 0.75)]!;

  if (velocity >= p75) return 'growing';
  if (velocity <= p25) return 'declining';
  return 'stable';
}
